import range from "lodash/range";

/**
 * A data structure that represents a navigable range from 0 to max (inclusive)
 * with support for skipping specific values during navigation.
 *
 * Key features:
 * - Navigate forward/backward through the range
 * - Automatically skip over designated values
 * - Never goes below 0 or above max
 * - Value 0 can never be skipped
 * - If max is skipped, finds the highest non-skipped value
 *
 * Example:
 *   const range = new SkippableRange(5); // 0, 1, 2, 3, 4, 5
 *   range.setSkippable([2, 4]);
 *   range.next(); // 1
 *   range.next(); // 3 (skips 2)
 *   range.next(); // 5 (skips 4)
 */
export class SkippableRange {
  private max: number;
  private current: number;
  private skippable: Set<number>;

  /**
   * Creates a new SkippableRange
   * @param max The maximum value in the range (inclusive)
   * @param initialCurrent The starting position (default: 0)
   * @param skippable Array of values to skip (0 will be ignored)
   */
  constructor(max: number, initialCurrent = 0, skippable: number[] = []) {
    if (max < 0) {
      throw new Error("max must be >= 0");
    }
    if (initialCurrent < 0 || initialCurrent > max) {
      throw new Error(`initialCurrent must be between 0 and ${max}`);
    }

    this.max = max;
    this.skippable = new Set(
      skippable.filter((n) => n !== 0 && n >= 0 && n <= max)
    );

    // If initial position is skippable, find a valid starting position
    if (this.skippable.has(initialCurrent)) {
      const next = this.findNextNonSkippable(initialCurrent);
      const prev = this.findPreviousNonSkippable(initialCurrent);

      if (next !== null) {
        this.current = next;
      } else if (prev !== null) {
        this.current = prev;
      } else {
        this.current = 0; // Fallback (0 can never be skipped)
      }
    } else {
      this.current = initialCurrent;
    }
  }

  /**
   * Gets the current value in the range
   */
  getCurrent(): number {
    return this.current;
  }

  /**
   * Sets the current value in the range. Returns true if the value is not skippable, false otherwise. Won't set the value if it's skippable or out of bounds.
   */
  setCurrent(value: number): boolean {
    if (value < 0 || value > this.max || this.skippable.has(value)) {
      return false;
    }
    this.current = value;
    return true;
  }

  /**
   * Gets the maximum value in the range
   */
  getMax(): number {
    return this.max;
  }

  /**
   * Gets the highest non-skipped value in the range
   */
  getLastApplicable(): number {
    let last = this.max;
    while (last > 0 && this.skippable.has(last)) {
      last--;
    }
    return last;
  }

  /**
   * Checks if a specific value is skipped
   */
  isSkipped(value: number): boolean {
    return this.skippable.has(value);
  }

  /**
   * Checks if currently at the first position (0)
   */
  isAtFirst(): boolean {
    return this.current === 0;
  }

  /**
   * Checks if currently at the last applicable (non-skipped) position
   */
  isAtLast(): boolean {
    return this.current === this.getLastApplicable();
  }

  /**
   * Moves to the next non-skipped value
   * @returns The new current value (unchanged if already at the end)
   */
  next(): number {
    const nextValue = this.findNextNonSkippable(this.current);
    if (nextValue !== null) {
      this.current = nextValue;
    }
    return this.current;
  }

  /**
   * Moves to the previous non-skipped value
   * @returns The new current value (unchanged if already at the start)
   */
  previous(): number {
    const prevValue = this.findPreviousNonSkippable(this.current);
    if (prevValue !== null) {
      this.current = prevValue;
    }
    return this.current;
  }

  /**
   * Sets which values should be skipped
   * @param numbers Array of values to skip (0 will be ignored)
   */
  setSkippable(numbers: number[]): void {
    this.skippable = new Set(
      numbers.filter((n) => n !== 0 && n >= 0 && n <= this.max)
    );

    // If current position is now skippable, move to a valid position
    if (this.skippable.has(this.current)) {
      const next = this.findNextNonSkippable(this.current);
      const prev = this.findPreviousNonSkippable(this.current);

      if (next !== null) {
        this.current = next;
      } else if (prev !== null) {
        this.current = prev;
      } else {
        this.current = 0; // Fallback (0 can never be skipped)
      }
    }
  }

  /**
   * Adds a single value to the skippable set
   * @param value The value to skip (0 will be ignored)
   */
  addSkippable(value: number): void {
    // Don't add 0 or values outside the range
    if (value === 0 || value < 0 || value > this.max) {
      return;
    }

    this.skippable.add(value);

    // If current position is now skippable, move to a valid position
    if (this.current === value) {
      const next = this.findNextNonSkippable(this.current);
      const prev = this.findPreviousNonSkippable(this.current);

      if (next !== null) {
        this.current = next;
      } else if (prev !== null) {
        this.current = prev;
      } else {
        this.current = 0; // Fallback (0 can never be skipped)
      }
    }
  }

  /**
   * Removes a single value from the skippable set
   * @param value The value to remove from skippable set
   */
  removeSkippable(value: number): void {
    this.skippable.delete(value);
  }

  /**
   * @returns the applicable values in the range as an array
   */
  getApplicableValues(): number[] {
    return range(this.max + 1).filter((n: number) => !this.skippable.has(n));
  }

  /**
   * Finds the next non-skipped value after the given position
   * @returns The next non-skipped value, or null if none exists
   */
  private findNextNonSkippable(from: number): number | null {
    const lastApplicable = this.getLastApplicable();
    let next = from + 1;

    while (next <= lastApplicable && this.skippable.has(next)) {
      next++;
    }

    return next <= lastApplicable ? next : null;
  }

  /**
   * Finds the previous non-skipped value before the given position
   * @returns The previous non-skipped value, or null if none exists
   */
  private findPreviousNonSkippable(from: number): number | null {
    let prev = from - 1;

    while (prev >= 0 && this.skippable.has(prev)) {
      prev--;
    }

    return prev >= 0 ? prev : null;
  }
}
